import { defineStore } from "pinia";
import App from "@bloglovin/inspire-engine/App";
import { STORY_ENTITY_TYPE, contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { $axios } from "@bloglovin/vue-framework";
import dayjs from "dayjs";
import SmartFeedEngine from "SmartFeed/smart-feed-engine";
import { PUBLISHING_PLATFORM_TYPE } from "%/dynamic-component-editor/constants/story-constants";
import { useSocialNetworksStore } from "@/stores/social-networks-store";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { usePostFeedStore } from "SmartFeed/stores/feed/post-feed-store";
import { describeRefusal } from "%/helpers/member-post-refusal";
import { useMemberPostFeedStore } from "SmartFeed/stores/feed/member-post-feed-store";
import { applyStoryFields, insertMemberPostIntoFeed } from "%/dynamic-component-editor/standard-components/base-story-card/member-post-feed-actions";
import { buildStoryEngagementEvent, engagementFieldsFromCommunityPost } from "%/dynamic-component-editor/helpers/story-engagement.mjs";
import { STORY_ENGAGEMENT_APP, STORY_THREAD_ENTITY_TYPE } from "%/dynamic-component-editor/constants/story-engagement-constants.mjs";
import { useOrgPostFeedStore } from "SmartFeed/stores/feed/org-post-feed-store";
import { useSyndicatedStoryFeedStore } from "SmartFeed/stores/feed/my-syndicated-story-feed-store";
import { useMyPublicationPostFeedStore } from "SmartFeed/stores/feed/my-publication-post-feed-store";
import {
  STORY_PUBLISH_STATUS_ACTIVE,
  STORY_PUBLISH_STATUS_DRAFT,
  STORY_PUBLISH_STATUS_NEEDS_EDITING,
  STORY_PUBLISH_STATUS_SCHEDULED,
  STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW,
  STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW,
} from "~/constants/story-status-constants";

const storyInFlight = new Map();

export const useStoryStore = defineStore("story-store", {
  state: () => ({
    story: {},
    stories: [],
    isLoadingCreateStory: false,
    isLoadingPublishSyndicationStory: false,
    uploadStoryThumbnailLoading: false,
    uploadStoryImageLoading: false,
    uploadStoryVideoLoading: false,
    uploadStoryAudioLoading: false,
    loadingAddHashtagToStory: false,
    errorAddHashtagToStory: false,
    updateStoryExclusivityIsLoading: false,
    updateStoryRequiresSyndicationApprovalContentIsLoading: false,
    updateStoryAllowRemixContentIsLoading: false,
    updateStoryTitleIsLoading: false,
    updateStoryAssignToPlatformIsLoading: false,
    updateStoryIsLoading: false,
    updatePostIsLoading: false,
    quickPostRefusal: null,
    isCreatingStoryVariant: false,
    updateStoryIsLoadingId: undefined,
    isLikeActionLoading: false,
    isVotingActionLoading: false,
    assignToPublishingPlatformModalOpen: false,
    assignToSyndicatedPublishingPlatformModalOpen: false,
    assignToExclusivePublishingPlatformModalOpen: false,
    isPublishingStory: false,
    isUnpublishingStory: false,
    forcePublishAfterAssigning: false,
    isSyndicatingActionLoading: false,
  }),
  getters: {
    orgId() {
      return useGeneralStore().orgId;
    },
    userId() {
      return useGeneralStore().userId;
    },
    isUserLoggedIn() {
      return useConfigurationStore().isSignupAndLoginEnabled && this.userId > 0;
    },
    isStoryEngagementOn() {
      return window.blclient.data.storyEngagement?.enabled === true;
    },
    renderDestinationId() {
      return Number(window.blclient.data.renderDestinationId ?? 0);
    },
    fanUrl() {
      return useGeneralStore().fanUrl;
    },
    storySegments() {
      const segmentArray = Object.values(this.story.segments);
      return this.story.segment_order.reduce((segmentItem, order) => {
        const segment = segmentArray.find(segmentData => segmentData.story_segment_id === segmentItem.story_segment_id);
        return [
          ...order,
          segment,
        ];
      }, []);
    },
  },
  actions: {
    async getOrFetchStory(storyId) {
      if (!storyId) {
        return null;
      }
      const cached = this.stories?.[storyId];
      if (cached?.storyId || cached?.story_id) {
        return cached;
      }
      if (storyInFlight.has(storyId)) {
        return await storyInFlight.get(storyId);
      }
      const promise = new Promise(resolve => {
        this.apiGet(
          "/story",
          { story_id: storyId },
          data => {
            if (!this.stories || typeof this.stories !== "object" || Array.isArray(this.stories)) {
              this.stories = {};
            }
            this.stories[storyId] = {
              ...data,
              storyTitle: data.storyTitle ?? data.title,
              authorName: data.authorName ?? data.authoringName,
              authorAvatarUrl: data.authorAvatarUrl ?? data.authoringAvatarUrl,
            };
            resolve(this.stories[storyId]);
          },
          () => resolve(null),
        );
      }).finally(() => {
        storyInFlight.delete(storyId);
      });
      storyInFlight.set(storyId, promise);
      return await promise;
    },
    triggerStoryAssignModal() {
      if (this.story.publishing_platform_id && this.story.publishing_platform_id > 0) {
        return;
      }
      this.assignToPublishingPlatformModalOpen = true;
    },
    triggerSyndicatedAssignModal() {
      this.assignToSyndicatedPublishingPlatformModalOpen = true;
    },
    triggerExclusiveAssignModal() {
      this.assignToExclusivePublishingPlatformModalOpen = true;
    },
    publishingStory(redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.publishStory(this.story.story_id, () => {
            this.navigateAfterPublishing(redirectAfterPublish);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
          useSocialNetworksStore().postMessage(
            this.orgId,
            this.story.publishing_platform_id,
            this.story.story_id,
            () => {
              this.navigateAfterPublishing(redirectAfterPublish);
            },
          );
          break;
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
          this.isPublishingStory = false;
          break;
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    submitForReviewStory(redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.submitStoryForReview(this.story.story_id, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    schedulePublishingStory(dateTime, redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.scheduleStory(this.story.story_id, dateTime, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_SCHEDULED);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    schedulePublishingStoryNeedsReview(dateTime, redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.scheduleStoryNeedsReview(this.story.story_id, dateTime, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    rejectPublishingStoryAfterReview(redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.rejectStoryAfterReview(this.story.story_id, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_NEEDS_EDITING);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    publishStoryFromMarketplace() {
      if (this.story.is_exclusive === 1) {
        this.triggerExclusiveAssignModal();
        return;
      }
      this.triggerSyndicatedAssignModal();
    },
    navigateAfterPublishing(redirectAfterPublish = true, statusType = "active") {
      if (!redirectAfterPublish) {
        useOrgPostFeedStore().reload();
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get("redirect");

      if (redirect) {
        window.location.href = `${window.location.origin}${redirect}`;
        return;
      }

      this.story.status = statusType;
    },
    createStory(platform = {}, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isLoadingCreateStory = true;
      this.apiPost("/story/create",
        {
          org_id: this.orgId,
          user_id: this.userId,
          publishing_platform_id: platform.publishing_platform_id || 0,
          publishing_platform_type: platform.publishing_platform_type ?? null,
          variant_type: platform.variant_type ?? "",
        },
        response => {
          window.location.href = `/stories/editor?story_id=${response.story_id}`;
          successHandler(response);
        },
        () => {
          errorHandler();
        },
        () => {
          this.isLoadingCreateStory = false;
        });
    },
    saveSessionDataAndRedirect(postData) {
      sessionStorage.setItem("postCreationData", JSON.stringify(postData));
      const encodedCurrentUri = encodeURIComponent(window.location.pathname);
      window.location.href = `${this.fanUrl}/login?redirect=${encodedCurrentUri}`;

    },
    createQuickStory(publicationId, postData, callback) {
      if (!this.isUserLoggedIn) {
        this.saveSessionDataAndRedirect(postData);
      }
      this.updatePostIsLoading = true;
      const data = {
        publication_id: publicationId,
        org_id: this.orgId,
        title: postData.title,
        segment: {
          "style": "BrandedParagraphElement",
          "data": {
            ...postData.content,
          },
          "editorId": Math.random().toString(36).substring(2, 15),
        },
        image_segment: postData.image_segment ?? {},
      };

      if (postData.image) {
        data.image_segment = {
          ...postData.image,
          "editorId": Math.random().toString(36).substring(2, 15),
        };
      }
      this.apiPost(`/publication/${publicationId}/quick-story`, {
        data,
      }, response => {
        usePostFeedStore().reload();
        useMemberPostFeedStore().reloadMemberPosts();
        insertMemberPostIntoFeed(publicationId, response?.story_id);
        this.updatePostIsLoading = false;
        this.quickPostRefusal = null;
        callback(true);
      }, error => {
        this.updatePostIsLoading = false;
        this.quickPostRefusal = describeRefusal(error);
        callback(false, this.quickPostRefusal);
      });
    },
    generateTextStoryContent(prompt, successCallback) {
      return this.apiPost("/story/generate/text", { prompt }, successCallback);
    },
    generateAiProductCollection(prompt, successCallback, errorCallback = () => {}) {
      return this.apiPost("/story/generate/products", { prompt }, successCallback, errorCallback);
    },
    generateTextStoryContentChanges(prompt, content, successCallback) {
      return this.apiPost("/story/generate/text/edit", { prompt, content }, successCallback);
    },
    updateStory(storyId, data, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryIsLoading = true;
      this.updateStoryIsLoadingId = storyId;

      this.apiPost(`/story/${storyId}`, {
        "story_version_id": this.story.current_story_version_id,
        "live_segment_order": data.liveSegmentOrder,
        "segments": data.segments,
      }, result => {
        this.story = {
          ...this.story,
          ...result,
        };
        this.updateStoryIsLoading = false;
        if (successHandler) {
          successHandler();
          return;
        }
        window.location.href = "/stories";
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryIsLoading = false;
        this.updateStoryIsLoadingId = undefined;
      });
    },
    removeStoryThumbnail(storyId, responseHandler = () => {}, finallyHandler = () => {}) {
      this.uploadStoryThumbnailLoading = true;
      this.apiPost(`/story/${storyId}/thumbnail/remove`, {}, response => {
        this.story.thumbnail = response.thumbnail;
        responseHandler(response);
      }, () => {}, () => {
        this.uploadStoryThumbnailLoading = false;
        finallyHandler();
      });
    },
    async uploadStoryThumbnail(storyId, selectedFile) {
      this.uploadStoryThumbnailLoading = true;
      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("image", selectedFile);

      return await $axios
          .post(`/story/${storyId}/thumbnail`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            this.story.thumbnail = response.data.thumbnail;
            return response;
          })
          .finally(() => {
            this.uploadStoryThumbnailLoading = false;
          });
    },
    async uploadStoryImage(selectedFile) {
      this.uploadStoryImageIsLoading = true;
      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("image", selectedFile);

      return await $axios
          .post("/story/image/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return response.data.imageUrl;
          })
          .finally(() => {
            this.uploadStoryImageIsLoading = false;
          });
    },
    async uploadStoryVideo(selectedFile) {
      this.uploadStoryVideoLoading = true;

      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("video", selectedFile);

      return await $axios
          .post("/story/video/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return {
              videoUrl: response.data.videoUrl,
              posterUrl: response.data.posterUrl,
            };
          })
          .finally(() => {
            this.uploadStoryVideoLoading = false;
          });
    },
    async uploadStoryAudio(selectedFile) {
      this.uploadStoryAudioLoading = true;

      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("audio", selectedFile);

      return await $axios
          .post("/story/audio/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return response.data.audioUrl;
          })
          .finally(() => {
            this.uploadStoryAudioLoading = false;
          });
    },
    addHashtagsToStory(hashtags, storyId, errorHandler = () => {
    }, successHandler = () => {
    }) {
      this.loadingAddHashtagToStory = true;
      return $axios.post(`/story/${storyId}/tag`,
        {
          hashtags,
        }).then(response => {
        this.story.timestamp_updated = dayjs().unix();
        this.story.hashtags = response.data.hashtags;
        successHandler();
      }).catch(() => {
        errorHandler();
      }).finally(() => {
        this.loadingAddHashtagToStory = false;
      });
    },
    removeHashtagFromStory(hashtagId, storyId, errorHandler = () => {
    }, successHandler = () => {
    }) {
      this.loadingAddHashtagToStory = true;
      return $axios.post(
        `/story/${storyId}/untag`,
        {
          hashtag_id: hashtagId,
        },
      ).then(response => {
        this.story.timestamp_updated = dayjs().unix();
        this.story.hashtags = response.data.hashtags;
        successHandler();
      }).catch(() => {
        errorHandler();
      }).finally(() => {
        this.loadingAddHashtagToStory = false;
      });
    },
    updateStoryExclusiveContentFlag(storyId, exclusive, successHandler = () => {
    }, errorHandler) {
      this.updateStoryExclusivityIsLoading = true;
      this.apiPost(`/story/${storyId}/exclusive`, {
        exclusive: exclusive ? 1 : 0,
      }, () => {
        this.story.exclusive = exclusive;
        this.story.timestamp_updated = dayjs().unix();
        this.updateStoryExclusivityIsLoading = false;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryExclusivityIsLoading = false;
      });
    },
    updateStoryAllowRemixContentFlag(storyId, allowRemixContent, successHandler = () => {
    }, errorHandler) {
      this.updateStoryAllowRemixContentIsLoading = true;
      this.apiPost(`/story/${storyId}/allow-remix`, {
        allow_remix: allowRemixContent ? 1 : 0,
      }, data => {
        this.story.allow_remix = allowRemixContent;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryAllowRemixContentIsLoading = false;
      });
    },
    updateStoryRequiresSyndicationApprovalContentFlag(storyId, requiresSyndicationApproval, successHandler = () => {
    }, errorHandler) {
      this.updateStoryRequiresSyndicationApprovalContentIsLoading = true;
      this.apiPost(`/story/${storyId}/requires-syndication-approval`, {
        requires_syndication_approval: requiresSyndicationApproval ? 1 : 0,
      }, data => {
        this.story.requires_syndication_approval = requiresSyndicationApproval;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryRequiresSyndicationApprovalContentIsLoading = false;
      });
    },
    updateStoryTitle(storyId, storyTitle, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryTitleIsLoading = true;
      this.apiPost(`/story/${storyId}/title`, {
        story_title: storyTitle,
      }, data => {
        this.story.title = data.title;
        this.story.timestamp_updated = data.timestamp_updated;
        this.story.status = data.status;
        successHandler();
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryTitleIsLoading = false;
      });
    },
    assignStoryToPublishingPlatform(storyId, platformType, platformId, variant = "", successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryAssignToPlatformIsLoading = true;
      this.apiPost(`/story/${storyId}/platform`, {
        publishing_platform_type: platformType,
        publishing_platform_id: platformId,
        variant_type: variant,
      }, data => {
        this.story.publishing_platform_type = platformType;
        this.story.publishing_platform_id = platformId;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler();
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryAssignToPlatformIsLoading = false;
      });
    },
    assignSyndicatedStoryToPublishingPlatform(storyId, platformType, platformId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryAssignToPlatformIsLoading = true;
      this.apiPost(`/story/${storyId}/platform`, {
        publishing_platform_type: platformType,
        publishing_platform_id: platformId,
      }, data => {
        this.story.publishing_platform_type = platformType;
        this.story.publishing_platform_id = platformId;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler();
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryAssignToPlatformIsLoading = false;
      });
    },
    createStoryVariant(storyId, variant, platformId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isCreatingStoryVariant = true;

      this.apiPost(`/story/${storyId}/variant`,
        {
          variant,
          platform_id: platformId,
        },
        data => {
          this.isCreatingStoryVariant = false;
          successHandler(data);
        },
        () => {
          this.isCreatingStoryVariant = false;
          errorHandler();
        });
    },
    publishStory(storyId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.story.status = STORY_PUBLISH_STATUS_ACTIVE;
      this.apiPost(`/story/${storyId}/publish`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    submitStoryForReview(storyId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.story.status = STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW;
      this.apiPost(`/story/${storyId}/submit/review`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    scheduleStory(storyId, dateTime, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.apiPost(`/story/${storyId}/${dateTime}/schedule/publish`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    scheduleStoryNeedsReview(storyId, dateTime, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.apiPost(`/story/${storyId}/${dateTime}/review/schedule`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    rejectStoryAfterReview(storyId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.apiPost(`/story/${storyId}/review/reject`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    unpublishStory(storyId, updateStatus) {
      this.isUnpublishingStory = true;
      this.apiPost(`/story/${storyId}/unpublish`, {}, () => {
        if (updateStatus) {
          this.story.status = STORY_PUBLISH_STATUS_DRAFT;
        }
      }, () => {
      }, () => {
        this.isUnpublishingStory = false;
        useOrgPostFeedStore().reload();
      });
    },
    sendStoryEngagement(storyId, action, onDone, onError) {
      this.apiPost(
        `/story/${storyId}/${action}`,
        {},
        response => {
          let fields;
          try {
            fields = engagementFieldsFromCommunityPost(response?.entities?.communityPost?.[storyId]);
          } catch (error) {
            onError(error);
            return;
          }
          onDone(fields);
        },
        onError,
      );
    },
    sendStoryEngagementEvent(storyId, kind, target, onDone, onError) {
      const { eventType, params } = buildStoryEngagementEvent(kind, storyId, target);
      this.triggerStoryEngagementEvent(eventType, params).then(() => onDone(this.readStoryThreadCounts(storyId)), onError);
    },
    triggerStoryEngagementEvent(eventType, params) {
      return new Promise((resolve, reject) => {
        const fired = App.get(STORY_ENGAGEMENT_APP).triggerEventWithOutcome(eventType, params);
        fired.serverOutcome.then(rejection => {
          if (rejection) {
            reject(rejection.error);
            return;
          }
          resolve(fired.params);
        });
      });
    },
    readStoryThreadCounts(storyId) {
      const engagement = App.get(STORY_ENGAGEMENT_APP);
      const pk = { storyId: Number(storyId) };
      if (!engagement.hasEntity(STORY_THREAD_ENTITY_TYPE, pk)) {
        return null;
      }
      const readCount = fieldName => engagement.getEntityField(STORY_THREAD_ENTITY_TYPE, pk, fieldName);
      return {
        likeCount: readCount("likeCount"),
        upvoteCount: readCount("upvoteCount"),
        downvoteCount: readCount("downvoteCount"),
        commentCount: readCount("commentCount"),
      };
    },
    showStoryCounts(storyId, storyEntityCounts, pageRecordCounts) {
      if (App.get(contextualAppName()).hasEntity(STORY_ENTITY_TYPE, { id: Number(storyId) })) {
        applyStoryFields(storyId, storyEntityCounts);
      }
      if (this.stories[storyId]) {
        Object.assign(this.stories[storyId], pageRecordCounts);
      }
    },
    recordStoryShare(storyId, onDone, onError) {
      this.apiPost(
        `/story/${storyId}/share`,
        {},
        response => onDone(Number(response.share_count)),
        onError,
      );
    },
    redirectToLoginToEngage() {
      const encodedCurrentUri = encodeURIComponent(window.location.pathname);
      window.location.href = `${this.fanUrl}/login?redirect=${encodedCurrentUri}`;
    },
    reportMemberPost(publicationId, storyId, reason, onDone, onError = () => {}) {
      this.apiPost(
        `/publication/${publicationId}/member-posts/${storyId}/report`,
        { reason },
        response => {
          onDone({ numReports: Number(response?.result?.num_reports ?? 0) });
        },
        onError,
      );
    },
    async likeStory(storyId) {
      this.isLikeActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/like`,
        {},
        response => {
          this.isLikeActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.like_status = response.entities.communityPost[storyId].likeStatus;
          this.story.num_likes = response.entities.communityPost[storyId].numLikes;
        },
        () => {
        },
        () => {
          this.isLikeActionLoading = false;
        },
      );
    },
    async unlikeStory(storyId) {
      this.isLikeActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/unlike`,
        {},
        response => {
          this.isLikeActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          // useLovedPostFeedStore().reload();
          this.story.like_status = response.entities.communityPost[storyId].likeStatus;
          this.story.num_likes = response.entities.communityPost[storyId].numLikes;
        },
        () => {
          this.isLikeActionLoading = false;
        },
      );
    },
    async upVoteStory(storyId) {
      this.isVotingActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/upvote`,
        {},
        response => {
          this.isVotingActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.vote_status = response.entities.communityPost[storyId].voteStatus;
          this.story.vote_score = response.entities.communityPost[storyId].voteScore;
        },
        () => {
        },
        () => {
          this.isVotingActionLoading = false;
        },
      );
    },
    async downVoteStory(storyId) {
      this.isVotingActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/downvote`,
        {},
        response => {
          this.isVotingActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.vote_status = response.entities.communityPost[storyId].voteStatus;
          this.story.vote_score = response.entities.communityPost[storyId].voteScore;
        },
        () => {
        },
        () => {
          this.isVotingActionLoading = false;
        },
      );
    },
    removeVoteStory(storyId) {
      this.isVotingActionLoading = true;
      return this.apiPost(
        `/story/${storyId}/remove-vote`,
        {},
        response => {
          this.isVotingActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.vote_status = response.entities.communityPost[storyId].voteStatus;
          this.story.vote_score = response.entities.communityPost[storyId].voteScore;
        },
        () => {
        },
        () => {
          this.isVotingActionLoading = false;
        },
      );
    },
    publishSyndicationStory(sourceStoryId, publicationId) {
      this.isLoadingPublishSyndicationStory = true;
      return this.apiPost(
        `/story/${sourceStoryId}/syndication/publish`,
        {
          "publication_id": publicationId,
        },
        response => {
          this.isLoadingPublishSyndicationStory = false;
          this.assignStoryToPublishingPlatform(
            response.story_id,
            response.publishing_platform_type,
            response.publishing_platform_id,
            "",
            () => {
              if (window.location.href.includes("syndicated-story")) {
                window.location.href = `${window.location.origin}/publication/${publicationId}/stories`;
              }
            },
          );
        },
        () => {
        },
        () => {
          this.isLoadingPublishSyndicationStory = false;
        },
      );
    },
    approveSyndicatedStory(storyId) {
      this.isSyndicatingActionLoading = true;
      return this.apiPost(
        `/story/${storyId}/syndication/approve`,
        {
          "story_id": storyId,
        },
        () => {
          this.isSyndicatingActionLoading = false;
          useSyndicatedStoryFeedStore().reload();
        },
        () => {
        },
        () => {
          this.isSyndicatingActionLoading = false;
        },
      );
    },
    rejectSyndicatedStory(storyId) {
      this.isSyndicatingActionLoading = true;
      return this.apiPost(
        `/story/${storyId}/syndication/reject`,
        {
          "story_id": storyId,
        },
        () => {
          this.isSyndicatingActionLoading = false;
          useSyndicatedStoryFeedStore().reload();
        },
        () => {
        },
        () => {
          this.isSyndicatingActionLoading = false;
        },
      );
    },
    deleteStory(storyId) {
      this.apiPost(`/story/${storyId}/delete`, {}, () => {
        useMyPublicationPostFeedStore().reload();
      }, () => {
      });
    },
  },
});
